import type { Category } from '../types';
import { FACTS } from '../facts';

const PCT = `${FACTS.taxSetAsidePct.low}–${FACTS.taxSetAsidePct.high}%`;

export const recordkeeping: Category = {
  slug: "recordkeeping-and-money",
  half: "business",
  accent: '#2E9E3A',
  icon: "ledger",
  label: { en: "Recordkeeping & money", es: "Registros y dinero" },
  blurb: {
    en: `Set aside ~${PCT} for taxes, log every mile, keep receipts. Simple systems that save you in April.`,
    es: `Aparta ~${PCT} para impuestos, registra cada milla, guarda los recibos. Sistemas simples que te salvan en abril.`,
  },
  guides: [
    {
      slug: "set-aside-for-taxes",
      irsLinks: ["1040-ES", "schedule-c"],
      related: [
        "taxes-101/quarterly-estimated-taxes",
        "taxes-101/self-employment-tax",
        "taxes-101/tax-calendar",
        "recordkeeping-and-money/receipts-and-bookkeeping",
      ],
      body: {
        en: {
          title: `Set aside ${PCT} for taxes`,
          summary: "Nobody withholds taxes from your payouts — you do it yourself, a little every week.",
          sections: [
            {
              heading: "Why this matters",
              paragraphs: [
                "As a Runner you're an independent contractor. Grocery List Plus pays you weekly through Stripe Connect, and nothing is withheld for income tax or self-employment tax. Whatever you owe in April comes out of money that already landed in your account.",
                "The fix is simple: move a slice of every payout somewhere separate before you can spend it.",
              ],
            },
            {
              heading: "How much",
              paragraphs: [
                `A common rule of thumb is ${PCT} of your profit — your fees and tips minus business expenses like mileage and the lane pass. Your real rate depends on your state, your other income and your household, so confirm the number with an accountant. Starting at ${FACTS.taxSetAsidePct.low}% and adjusting after your first quarter beats guessing.`,
              ],
            },
            {
              heading: "Make it automatic",
              bullets: [
                "Open a second account (savings or a separate checking) just for taxes.",
                "The day your weekly payout lands, transfer your percentage.",
                "Don't touch it except to pay the IRS or your state.",
                "Each quarter, use it to pay your estimated taxes — mid-April, mid-June, mid-September and mid-January.",
              ],
            },
            {
              heading: "Where the numbers come from",
              paragraphs: [
                "Your pay statement shows fees, tips and the lane pass as separate lines, so your weekly profit before mileage is right there. Keep the mileage log current and your quarter-end math takes minutes, not a weekend.",
              ],
            },
          ],
          actions: [
            "Open a separate tax account this week.",
            `Pick your percentage (start at ${FACTS.taxSetAsidePct.low}% if unsure) and write it down.`,
            "Set a calendar reminder for the next quarterly due date.",
            "Book 30 minutes with an accountant if you've never filed self-employment income.",
          ],
        },
        es: {
          title: `Aparta ${PCT} para impuestos`,
          summary: "Nadie retiene impuestos de tus pagos: lo haces tú, un poco cada semana.",
          sections: [
            {
              heading: "Por qué importa",
              paragraphs: [
                "Como Runner eres contratista independiente. Lista de Compras Plus te paga cada semana por Stripe Connect y no se retiene nada para el impuesto sobre la renta ni el impuesto sobre el trabajo por cuenta propia. Lo que debas en abril sale de dinero que ya llegó a tu cuenta.",
                "La solución es simple: mueve una parte de cada pago a un lugar aparte antes de poder gastarla.",
              ],
            },
            {
              heading: "Cuánto",
              paragraphs: [
                `Una regla práctica común es ${PCT} de tu ganancia: tus tarifas y propinas menos los gastos del negocio, como las millas y el lane pass. Tu tasa real depende de tu estado, tus otros ingresos y tu hogar, así que confirma la cifra con un contador. Empezar en ${FACTS.taxSetAsidePct.low}% y ajustar después del primer trimestre es mejor que adivinar.`,
              ],
            },
            {
              heading: "Hazlo automático",
              bullets: [
                "Abre una segunda cuenta (de ahorros o una corriente aparte) solo para impuestos.",
                "El día que llegue tu pago semanal, transfiere tu porcentaje.",
                "No la toques salvo para pagarle al IRS o a tu estado.",
                "Cada trimestre, úsala para pagar tus impuestos estimados: mediados de abril, junio, septiembre y enero.",
              ],
            },
            {
              heading: "De dónde salen los números",
              paragraphs: [
                "Tu estado de pago muestra tarifas, propinas y lane pass en líneas separadas, así que tu ganancia semanal antes de millas está ahí mismo. Mantén el registro de millas al día y las cuentas de fin de trimestre toman minutos, no un fin de semana.",
              ],
            },
          ],
          actions: [
            "Abre una cuenta aparte para impuestos esta semana.",
            `Elige tu porcentaje (empieza en ${FACTS.taxSetAsidePct.low}% si no estás seguro) y anótalo.`,
            "Pon un recordatorio en el calendario para la próxima fecha trimestral.",
            "Reserva 30 minutos con un contador si nunca has declarado ingresos por cuenta propia.",
          ],
        },
      },
    },
    {
      slug: "mileage-log-habit",
      irsLinks: ["pub-463"],
      related: ["deductions/mileage", "taxes-101/schedule-c-basics", "recordkeeping-and-money/receipts-and-bookkeeping"],
      body: {
        en: {
          title: "The mileage log habit",
          summary: "Miles are usually a Runner's biggest deduction — but only the miles you wrote down.",
          sections: [
            {
              heading: "Why the log matters",
              paragraphs: [
                "Every mile you drive for deliveries can reduce your taxable profit through the standard mileage rate — the current rate is in IRS Pub. 463. The catch: the IRS expects a record kept at the time. A log you rebuild in March from memory is weak; a log you kept as you went is strong.",
              ],
            },
            {
              heading: "What to record",
              bullets: [
                "The date.",
                "Where you started and where you ended (a store name and a neighborhood are enough).",
                "Miles driven for that run or shift.",
                "The business purpose (\"Grocery List Plus deliveries\" is fine).",
                "Your odometer reading on January 1 and December 31.",
              ],
            },
            {
              heading: "Which miles count",
              paragraphs: [
                "Driving from the store to a customer, and between customers, counts. Your commute from home to your first stop generally does not, and personal errands mixed into a shift never do. When in doubt, log it and mark it — an accountant can sort the gray areas later, but only if the miles were written down.",
              ],
            },
            {
              heading: "Make it a two-minute ritual",
              paragraphs: [
                "Download the mileage log in Tools & templates, keep it on your phone, and fill in one row when you finish each shift. Some Runners prefer a tracking app; either is fine as long as it's kept at the time and you can export it.",
              ],
            },
          ],
          actions: [
            "Write down your odometer reading today.",
            "Download the mileage log from Tools & templates.",
            "Log every shift for the next seven days without missing one.",
            "Compare your logged miles with your run history in the portal to catch gaps.",
          ],
        },
        es: {
          title: "El hábito del registro de millas",
          summary: "Las millas suelen ser la mayor deducción de un Runner, pero solo las que anotaste.",
          sections: [
            {
              heading: "Por qué importa el registro",
              paragraphs: [
                "Cada milla que manejas para entregas puede reducir tu ganancia gravable mediante la tarifa estándar por milla; la tarifa vigente está en la Pub. 463 del IRS. El detalle: el IRS espera un registro hecho en el momento. Un registro que reconstruyes en marzo de memoria es débil; uno que llevaste sobre la marcha es sólido.",
              ],
            },
            {
              heading: "Qué anotar",
              bullets: [
                "La fecha.",
                "Dónde empezaste y dónde terminaste (basta el nombre de la tienda y el vecindario).",
                "Millas manejadas en esa ruta o turno.",
                "El propósito del negocio (\"entregas de Lista de Compras Plus\" es suficiente).",
                "La lectura del odómetro el 1 de enero y el 31 de diciembre.",
              ],
            },
            {
              heading: "Qué millas cuentan",
              paragraphs: [
                "Manejar de la tienda a un cliente, y entre clientes, cuenta. El trayecto de tu casa a la primera parada por lo general no cuenta, y los mandados personales mezclados en un turno nunca cuentan. Si tienes duda, anótalo y márcalo: un contador puede resolver las zonas grises después, pero solo si las millas quedaron escritas.",
              ],
            },
            {
              heading: "Conviértelo en un ritual de dos minutos",
              paragraphs: [
                "Descarga el registro de millas en Herramientas y plantillas, tenlo en tu teléfono y llena una fila al terminar cada turno. Algunos Runners prefieren una app de seguimiento; cualquiera sirve mientras se lleve en el momento y puedas exportarla.",
              ],
            },
          ],
          actions: [
            "Anota hoy la lectura de tu odómetro.",
            "Descarga el registro de millas en Herramientas y plantillas.",
            "Registra cada turno de los próximos siete días sin saltarte ninguno.",
            "Compara tus millas registradas con tu historial de rutas en el portal para detectar huecos.",
          ],
        },
      },
    },
    {
      slug: "receipts-and-bookkeeping",
      irsLinks: ["schedule-c"],
      related: [
        "deductions/phone-and-data",
        "deductions/bags-coolers-gear",
        "deductions/tolls-and-parking",
        "taxes-101/what-counts-as-income",
      ],
      body: {
        en: {
          title: "Receipts and simple bookkeeping",
          summary: "A folder, a spreadsheet and ten minutes a week is a real bookkeeping system.",
          sections: [
            {
              heading: "What you don't need to track",
              paragraphs: [
                "At chain-store stops you pay with a Grocery List Plus-funded virtual card — never your own money — so groceries are not your expense and not your income. Your pay statement already lists your fees, tips and the lane pass. Most of your bookkeeping is done for you.",
              ],
            },
            {
              heading: "What you do track",
              bullets: [
                "Your phone and data plan (the business share).",
                "Insulated bags, coolers, carts and other gear.",
                "Tolls and parking on runs.",
                "Phone mounts, chargers and car accessories used for deliveries.",
                `The lane pass — $${FACTS.lanePassPerDeliveryUsd} per delivery, capped at $${FACTS.lanePassWeeklyMaxUsd} a week. It's a business expense and appears on every statement.`,
              ],
            },
            {
              heading: "Receipts",
              paragraphs: [
                "Snap a photo of each receipt the day you get it and drop it in one folder, on your phone or in the cloud. Name it by date and what it was. Paper fades; photos don't.",
                "Keep records for at least three years after you file — an accountant may suggest longer.",
              ],
            },
            {
              heading: "The weekly ten minutes",
              paragraphs: [
                "Open the expense tracker in Tools & templates. Add the week's receipts, note your payout from your statement, and check that the mileage log is current. At quarter end you'll have everything for estimated taxes; in January your Schedule C is mostly filled in.",
              ],
            },
          ],
          actions: [
            "Create one receipts folder and photograph every receipt from this week.",
            "Download the expense tracker from Tools & templates.",
            "Block ten minutes on the same day each week for bookkeeping.",
            "Ask an accountant which of your expenses are fully deductible and which are partial.",
          ],
        },
        es: {
          title: "Recibos y contabilidad simple",
          summary: "Una carpeta, una hoja de cálculo y diez minutos a la semana son un sistema de contabilidad de verdad.",
          sections: [
            {
              heading: "Lo que no necesitas registrar",
              paragraphs: [
                "En las paradas de tiendas de cadena pagas con una tarjeta virtual financiada por Lista de Compras Plus, nunca con tu propio dinero, así que los víveres no son tu gasto ni tu ingreso. Tu estado de pago ya lista tus tarifas, propinas y el lane pass. La mayor parte de tu contabilidad ya está hecha.",
              ],
            },
            {
              heading: "Lo que sí registras",
              bullets: [
                "Tu teléfono y plan de datos (la parte del negocio).",
                "Bolsas térmicas, hieleras, carritos y otro equipo.",
                "Peajes y estacionamiento durante las rutas.",
                "Soportes de teléfono, cargadores y accesorios del auto usados para entregas.",
                `El lane pass: $${FACTS.lanePassPerDeliveryUsd} por entrega, con tope de $${FACTS.lanePassWeeklyMaxUsd} a la semana. Es un gasto del negocio y aparece en cada estado de pago.`,
              ],
            },
            {
              heading: "Recibos",
              paragraphs: [
                "Tómale foto a cada recibo el mismo día y guárdala en una sola carpeta, en tu teléfono o en la nube. Nómbrala con la fecha y lo que fue. El papel se borra; las fotos no.",
                "Conserva los registros al menos tres años después de declarar; un contador puede sugerir más tiempo.",
              ],
            },
            {
              heading: "Los diez minutos semanales",
              paragraphs: [
                "Abre el registro de gastos en Herramientas y plantillas. Agrega los recibos de la semana, anota tu pago según tu estado de cuenta y revisa que el registro de millas esté al día. Al cierre del trimestre tendrás todo para los impuestos estimados; en enero tu Anexo C estará casi completo.",
              ],
            },
          ],
          actions: [
            "Crea una carpeta de recibos y fotografía todos los recibos de esta semana.",
            "Descarga el registro de gastos en Herramientas y plantillas.",
            "Reserva diez minutos el mismo día cada semana para la contabilidad.",
            "Pregúntale a un contador cuáles de tus gastos son deducibles por completo y cuáles solo en parte.",
          ],
        },
      },
    },
  ],
};
